import { useState, useEffect, use } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useMediaQuery, useTheme } from '@mui/system';

// Material UI imports
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import MicNoneOutlinedIcon from '@mui/icons-material/MicNoneOutlined';

// Local imports
import { containerStyles, topBoxStyles } from '../Dashboard/Dashboard';
import { api } from '../../api';
import { useAuth } from '../AuthContext'
import Error from "../Error/Error";
import Loading from '../Loading';
import SurveyResponsesTabPanel from './SurveyResponsesTabPanel';

const a11yProps = (index) => {
  return {
    id: `topic-tab-${index}`,
    'aria-controls': `topic-tabpanel-${index}`,
  };
}

const SurveyResponses = () => {
    const [topics, setTopics] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [responses, setResponses] = useState({});
    const [tabValue, setTabValue] = useState(1);
    const [loadingResponses, setLoadingResponses] = useState(true);

    const { isAdmin, authLoading } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        const fetchTopics = async () => {
            try {
                const data = await api.get('/survey/topics');
                setTopics(data);
                setLoading(false);
            } catch (err) {
                console.error(err);
                setError('Error fetching survey topics. Please try again later.');
            }
        }
        fetchTopics();
    }, []);

    useEffect(() => {
        const fetchResponses = async () => {
            try {
                const data = await api.get('/survey/responses');
                setResponses(data);
                setLoadingResponses(false);
            } catch (err) {
                console.error(err);
                setError('Error fetching survey responses. Please try again later.');
            }
        }
        fetchResponses();
    }, []);

    const countTextAndAudioResponses = (responses) => {
        let textCount = 0;
        let audioCount = 0;
        responses.forEach(r => {
            if (r.textResponse && r.textResponse.trim() !== '') {
                textCount++;
            }
            if (r.audios3Key) {
                audioCount++;
            }
        })
        return { textCount, audioCount };
    }

    const handleTabChange = (ev, newVal) => {
        setTabValue(newVal);
    }

    if (authLoading) {
        return <Loading />;
    }
    if (!isAdmin) {
        return <Error message="You do not have permission to view this page." />;
    }

    if (error) {
        return <Error message={error} />;
    }

    return (
        <Box sx={{background: 'linear-gradient(0.25turn, rgb(078, 042, 132), #caceab, #836EAA)', padding: {xs: 2, sm: 4 }, mt: {xs: 2, sm: 4 }, minHeight: '90vh' }}>
            <Box sx={{ ...containerStyles, borderRadius: 2, padding: {xs: 1, sm: 3 }, backgroundColor: '#f5f5f5' }}>   
                <Typography variant={isMobile ? 'h5' : 'h3'} sx={{ textAlign: 'center', marginBottom: { xs: 2, sm: 4 }, fontWeight: '700', color: 'rgb(118, 93, 160)' }}>Survey Responses Dashboard</Typography>

                {/* Topic cards */}
                {loading ? <Loading /> : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', marginBottom: 2 }}>
                        {topics.map((topic) => (
                            <Box sx={{ ...topBoxStyles, mb: 2, textAlign: 'left' }} key={topic.id}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between'}}>
                                    <Box 
                                    component="img" 
                                    src={topic.imageUrl} 
                                    alt={topic.topicName} 
                                    sx={{ 
                                        flex: 1,
                                        width: '70px', 
                                        height: '70px', 
                                        mr: 1,
                                        borderRadius: '8px',
                                        objectFit: 'cover'
                                    }} />
                                    <Typography variant='subtitle1' sx={{ flex: 2, ml: 1, fontWeight: '700' }}>{topic.topicName}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                                    <Typography variant='body1'>Total Responses</Typography>
                                    <Typography variant='body1'>
                                        {responses[topic.id]?.length || 0}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                    <Typography variant='body1'>
                                        <ChatBubbleOutlineOutlinedIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                                        Text
                                    </Typography>
                                    <Typography variant='body1'>
                                        {countTextAndAudioResponses(responses[topic.id] || []).textCount}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                    <Typography variant='body1'>
                                        <MicNoneOutlinedIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                                        Audio
                                    </Typography>
                                    <Typography variant='body1'>
                                        {countTextAndAudioResponses(responses[topic.id] || []).audioCount}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>

            <Box sx={containerStyles}>
                {/* Topics Tabs */}
                <Tabs 
                    indicatorColor='rgb(0, 0, 0, 0)' 
                    variant="scrollable" 
                    scrollButtons="auto" 
                    value={tabValue} 
                    onChange={handleTabChange} 
                    aria-label="topic tabs">
                    {topics.length > 0 ?
                        topics.map((topic, idx) => {
                            return (
                                <Tab label={topic.topicName} key={topic.id} value={topic.id} {...a11yProps(topic.id)}/>
                            )
                        })
                    : 'No topics found.'}
                </Tabs>
            </Box>

            {loadingResponses ? <Loading /> : null}
            {Object.keys(responses).length > 0 && Object.entries(responses).map(([questionId, resp], idx) => {
                return (
                    <SurveyResponsesTabPanel
                        value={tabValue} 
                        key={questionId}
                        index={parseInt(questionId)}
                        responses={resp}
                        questionData={topics.find(t => t.id === parseInt(questionId))}
                    />
                )
            })}
        </Box>
    )
}

export default SurveyResponses;